import { supabase } from '../lib/supabase';
import { adsApiService } from './adsApiService'; // Import adsApiService

export interface AdPost {
  id: string;
  type: 'ad';
  ad_id: string;
  campaign_id: string;
  title: string;
  description: string;
  media: Array<{
    id: string;
    type: 'image' | 'video';
    url: string;
    file_name: string;
  }>;
  author: {
    id: string;
    username: string;
    full_name: string;
    avatar_url: string | null;
    is_premium: boolean;
  };
  call_to_action: string;
  destination_url?: string;
  is_sponsored: true;
  created_at: string;
  likes: number;
  comments: Array<Record<string, unknown>>;
  views: number;
  category: string;
  tags: string[];
}

export const adsDisplayService = {
  /**
   * Get active ads for display in main app
   */
  async getActiveAdsForUser(userId?: string, limit: number = 3): Promise<AdPost[]> {
    try {
      console.log('🎯 AdsDisplayService: Fetching ads for user:', userId, 'limit:', limit);

      // Call the RPC function to get displayable ads
      // NOTE: This RPC function might need updates on the Supabase side
      // to fetch ads based on SAC balance and new schema.
      const { data: adsData, error } = await supabase.rpc('get_displayable_ads', {
        target_user_id: userId || null,
        limit_count: limit
      });

      if (error) {
        console.error('❌ RPC Error fetching ads:', error);
        return [];
      }

      if (!adsData || adsData.length === 0) {
        console.log('ℹ️ No active ads found in database');
        return [];
      }

      console.log('✅ Found', adsData.length, 'active ads:', adsData.map(ad => ({
        adId: ad.ad_id,
        campaignName: ad.campaign_name,
        creativeTitle: ad.creative_title,
        // budget: ad.daily_budget, // daily_budget might be removed/changed
        impressionsToday: ad.impressions_today
      })));

      return this.transformRpcAdsToPostFormat(adsData);

    } catch (err) {
      console.error('❌ Unexpected error getting ads:', err);
      return [];
    }
  },

  /**
   * Transform RPC ads data to AdPost format
   */
  transformRpcAdsToPostFormat(ads: Array<Record<string, unknown>>): AdPost[] {
    console.log('🔄 Transforming', ads.length, 'ads to post format');
    
    const transformedAds = ads.map((ad, index) => {
      console.log(`📝 Transforming ad ${index + 1}:`, {
        adId: ad.ad_id,
        campaignName: ad.campaign_name,
        creativeTitle: ad.creative_title,
        mediaUrls: ad.media_urls,
        callToAction: ad.call_to_action,
        // budget: ad.daily_budget // daily_budget might be removed/changed
      });

      const transformedAd: AdPost = {
        id: `ad_post_${ad.ad_id}`,
        type: 'ad',
        ad_id: ad.ad_id,
        campaign_id: ad.campaign_id,
        title: ad.creative_title || ad.campaign_name || 'Anuncio Promocionado',
        description: ad.creative_body || 'Descubre esta increíble propuesta artística.',
        media: this.processAdMedia(ad.media_urls, ad.thumbnail_url, ad.creative_type),
        author: {
          id: ad.account_id || 'advertiser',
          username: `anunciante_${ad.account_id?.substring(0, 8) || 'oficial'}`,
          full_name: 'Anunciante Verificado',
          avatar_url: null,
          is_premium: true,
        },
        call_to_action: ad.call_to_action || 'learn_more',
        destination_url: ad.destination_url,
        is_sponsored: true,
        created_at: ad.created_at || new Date().toISOString(),
        likes: 0,
        comments: [],
        views: parseInt(ad.impressions_today) || 0,
        category: this.inferCategoryFromCreative(ad.creative_type, ad.creative_title),
        tags: this.generateAdTags(ad.creative_title, ad.creative_body),
      };

      console.log(`✅ Transformed ad ${index + 1}:`, {
        id: transformedAd.id,
        title: transformedAd.title,
        mediaCount: transformedAd.media.length,
        category: transformedAd.category,
        views: transformedAd.views
      });

      return transformedAd;
    }).filter(ad => {
      const isValid = ad.title && ad.description && ad.media.length > 0;
      if (!isValid) {
        console.warn('⚠️ Filtered out invalid ad:', {
          title: ad.title,
          description: ad.description,
          mediaCount: ad.media.length
        });
      }
      return isValid;
    });

    console.log(`✅ Final transformed ads: ${transformedAds.length}/${ads.length} valid ads`);
    return transformedAds;
  },

  /**
   * Process media URLs from creative
   */
  processAdMedia(mediaUrls: unknown, thumbnailUrl: string | null, creativeType?: string): AdPost['media'] {
    const media: AdPost['media'] = [];

    console.log('🖼️ Processing media:', { mediaUrls, thumbnailUrl, creativeType });

    // Handle media_urls as array or JSON
    let urls: string[] = [];
    if (Array.isArray(mediaUrls)) {
      urls = mediaUrls.filter(url => url && typeof url === 'string');
    } else if (typeof mediaUrls === 'string') {
      try {
        const parsed = JSON.parse(mediaUrls);
        if (Array.isArray(parsed)) {
          urls = parsed.filter(url => url && typeof url === 'string');
        }
      } catch (_e) {
        void _e;
        // If parsing fails, treat as single URL
        if (mediaUrls.trim()) {
          urls = [mediaUrls.trim()];
        }
      }
    }

    if (urls.length > 0) {
      urls.forEach((url, index) => {
        if (url && url.trim()) {
          media.push({
            id: `ad_media_${index}`,
            type: creativeType === 'video' ? 'video' : 'image',
            url: url.trim(),
            file_name: `ad_creative_${index}.${creativeType === 'video' ? 'mp4' : 'jpg'}`,
          });
        }
      });
    } else if (thumbnailUrl && thumbnailUrl.trim()) {
      media.push({
        id: 'ad_media_thumbnail',
        type: 'image',
        url: thumbnailUrl.trim(),
        file_name: 'ad_thumbnail.jpg',
      });
    }

    console.log('✅ Processed media result:', media);
    return media;
  },

  /**
   * Infer category from creative content
   */
  inferCategoryFromCreative(creativeType?: string, title?: string): string {
    const titleLower = (title || '').toLowerCase();
    
    if (titleLower.includes('música') || titleLower.includes('concierto')) return 'music';
    if (titleLower.includes('foto') || titleLower.includes('fotografía')) return 'photography';
    if (titleLower.includes('diseño') || titleLower.includes('gráfico')) return 'digital_design';
    if (titleLower.includes('pintura') || titleLower.includes('exposición')) return 'painting';
    if (titleLower.includes('escultura')) return 'sculpture';
    if (titleLower.includes('tatuaje')) return 'tattoo';
    if (titleLower.includes('moda')) return 'fashion_design';
    if (titleLower.includes('danza') || titleLower.includes('baile')) return 'dance';
    if (titleLower.includes('escritura') || titleLower.includes('libro')) return 'writing';
    if (titleLower.includes('maquillaje')) return 'makeup';
    
    return creativeType === 'video' ? 'filmmaking' : 'digital_design';
  },

  /**
   * Generate relevant tags
   */
  generateAdTags(title?: string, body?: string): string[] {
    const text = `${title || ''} ${body || ''}`.toLowerCase();
    const tags = new Set<string>();

    const keywords = ['arte', 'diseño', 'creatividad', 'profesional', 'único', 'original', 'moderno', 'premium'];
    keywords.forEach(keyword => {
      if (text.includes(keyword)) {
        tags.add(keyword);
      }
    });

    tags.add('promocionado');
    return Array.from(tags).slice(0, 5);
  },

  /**
   * Track ad impression
   */
  async trackAdImpression(adId: string, _viewerId?: string) {
    try {
      void _viewerId;
      const response = await adsApiService.trackAdEvent(adId, 'impression');

      if (!response.success) {
        console.error('❌ Error tracking ad impression (adsApiService):', response.error);
      } else {
        console.log('✅ Ad impression tracked successfully via adsApiService for ad:', adId);
      }
    } catch (err) {
      console.error('❌ Unexpected error in trackAdImpression:', err);
    }
  },

  /**
   * Track ad click
   */
  async trackAdClick(adId: string, _viewerId?: string) {
    try {
      void _viewerId;
      const response = await adsApiService.trackAdEvent(adId, 'click');

      if (!response.success) {
        console.error('❌ Error tracking ad click:', response.error);
      } else {
        console.log('✅ Ad click tracked successfully for ad:', adId);
      }
    } catch (err) {
      console.error('❌ Unexpected error in trackAdClick:', err);
    }
  },

  getAdTimeBlockMs(): number {
    const value = (globalThis as unknown as { __AD_TIME_BLOCK_MS__?: unknown }).__AD_TIME_BLOCK_MS__;
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
    return 30_000;
  },

  async trackAdTimeBlock(params: { adId: string; viewerKey: string; placement?: string }) {
    try {
      const blockMs = this.getAdTimeBlockMs();
      const windowKey = Math.floor(Date.now() / blockMs);

      const response = await adsApiService.recordAdTimeBlock({
        adId: params.adId,
        viewerKey: params.viewerKey,
        windowKey,
        placement: params.placement ?? 'feed',
      });

      if (!response.success) {
        console.error('❌ Error recording ad time block:', response.error);
      } else if (response.data?.charged) {
        console.log('✅ Ad time block charged:', { adId: params.adId, windowKey });
      }

      return response;
    } catch (err) {
      console.error('❌ Unexpected error in trackAdTimeBlock:', err);
      return { success: false, error: (err as Error).message };
    }
  },

  /**
   * Get or generate session ID
   */
  getSessionId(): string {
    let sessionId = sessionStorage.getItem('ads_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2)}`;
      sessionStorage.setItem('ads_session_id', sessionId);
    }
    return sessionId;
  },

  /**
   * Mix organic posts with ads based on budget availability
   */
  async mixPostsWithAds(organicPosts: Array<Record<string, unknown>>, userId?: string, adFrequency: number = 4): Promise<Array<Record<string, unknown>>> {
    if (organicPosts.length < adFrequency) {
      console.log('ℹ️ Not enough posts to insert ads yet:', organicPosts.length);
      return organicPosts;
    }

    try {
      console.log('🔀 Mixing posts with ads. Organic posts:', organicPosts.length);
      
      // Calculate how many ads we can show based on post count
      const adsNeeded = Math.min(
        Math.ceil(organicPosts.length / adFrequency),
        5 // Maximum 5 ads per feed load
      );
      
      const ads = await this.getActiveAdsForUser(userId, adsNeeded);
      
      if (ads.length === 0) {
        console.log('ℹ️ No ads available, returning organic posts only');
        return organicPosts;
      }

      console.log('📽️ Got', ads.length, 'ads to mix with', organicPosts.length, 'organic posts');

      const mixedContent: Array<Record<string, unknown>> = [];
      let adIndex = 0;

      for (let i = 0; i < organicPosts.length; i++) {
        mixedContent.push(organicPosts[i]);
        
        // Insert an ad after every 'adFrequency' posts
        if ((i + 1) % adFrequency === 0 && adIndex < ads.length) {
          const adToInsert = ads[adIndex];
          mixedContent.push(adToInsert);
          adIndex++;
        }
      }

      console.log('✅ Final mixed content:', mixedContent.length, 'posts total (', ads.length, 'ads inserted)');
      return mixedContent;
    } catch (err) {
      console.error('❌ Error mixing posts with ads:', err);
      return organicPosts; // Fallback to original posts
    }
  },

  /**
   * Check if user has available budget for ads
   */
  async checkBudgetAvailability(adAccountId: string): Promise<boolean> {
    try {
      const accounts = await adsApiService.getAdAccounts();
      const adAccount = accounts.find(acc => acc.id === adAccountId);

      if (!adAccount) {
        console.error('Error: Ad account not found for budget check.');
        return false;
      }

      // Assuming a minimum balance is required for an ad to be considered "active"
      // For simplicity, let's say an ad account needs at least 1 SAC to be considered active.
      const hasAvailableBudget = adAccount.sac_balance > 0; 
      
      console.log('💰 Budget check:', {
        accountId: adAccountId,
        sacBalance: adAccount.sac_balance,
        hasAvailableBudget
      });

      return hasAvailableBudget;
    } catch (err) {
      console.error('Error in budget check:', err);
      return false;
    }
  },

  /**
   * Get ad performance metrics
   */
  async getAdPerformance(adId: string, days: number = 30) {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data: metrics, error } = await supabase
        .from('ad_metrics')
        .select('*')
        .eq('ad_id', adId)
        .gte('date', startDate.toISOString().split('T')[0])
        .order('date', { ascending: false });

      if (error) throw error;

      const totalMetrics = metrics.reduce((acc, metric) => ({
        impressions: acc.impressions + (metric.impressions || 0),
        clicks: acc.clicks + (metric.clicks || 0),
        spend: acc.spend + (metric.spend || 0),
        reach: Math.max(acc.reach, metric.reach || 0)
      }), { impressions: 0, clicks: 0, spend: 0, reach: 0 });

      const ctr = totalMetrics.impressions > 0 ? (totalMetrics.clicks / totalMetrics.impressions) * 100 : 0;
      const cpc = totalMetrics.clicks > 0 ? totalMetrics.spend / totalMetrics.clicks : 0;
      const cpm = totalMetrics.impressions > 0 ? (totalMetrics.spend / totalMetrics.impressions) * 1000 : 0; // Calculate CPM in SAC

      return {
        ...totalMetrics,
        ctr: Number(ctr.toFixed(4)),
        cpc: Number(cpc.toFixed(4)),
        cpm: Number(cpm.toFixed(4)),
        days: metrics.length
      };
    } catch (err) {
      console.error('Error getting ad performance:', err);
      return null;
    }
  }
};
